/**
 * Modal for adding a dealer plate. Two tabs sit on top of the same
 * destination row in `dealer_plates` (Decision 3.3 in the Slice 6 plan):
 *
 *   - Upload PNG: the operator already designed a plate in Photoshop
 *     or similar; INVI just stores the bytes.
 *   - Generate from Logo: INVI composes a logo-on-white plate from the
 *     dealer's uploaded logo. The Generate tab is disabled when the
 *     dealer has no logo on file (the API would reject it anyway,
 *     disabling the tab is the friendlier feedback).
 */
import React, { useState } from "react"

import { THEME } from "../../config/theme"
import {
  DealerLogo,
  DealerPlate,
  generateDealerPlate,
  uploadDealerPlate,
} from "../../api/dealerPlates"

type Mode = "upload" | "generate"

interface Props {
  dealerGroupId: number
  dealerLogo: DealerLogo | null
  onClose: () => void
  onSaved: (plate: DealerPlate) => void
}

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg"


export default function AddPlateModal({
  dealerGroupId, dealerLogo, onClose, onSaved,
}: Props) {
  // The Generate tab is the friendlier default — most operators want
  // INVI to do the work — but only when there's a logo to generate
  // from. Otherwise start on Upload so the operator isn't staring at
  // a disabled tab on first open.
  const [mode, setMode] = useState<Mode>(dealerLogo ? "generate" : "upload")
  const [name, setName] = useState<string>("")
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleFileChange = (incoming: File | null) => {
    setFile(incoming)
    setPreviewUrl((current) => {
      if (current) URL.revokeObjectURL(current)
      return incoming ? URL.createObjectURL(incoming) : null
    })
  }

  const canSave = (() => {
    if (!name.trim()) return false
    if (mode === "upload") return file !== null
    return dealerLogo !== null
  })()

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError("Plate name is required")
      return
    }
    setSaving(true)
    setError(null)
    try {
      const plate = mode === "upload"
        ? await uploadDealerPlate(dealerGroupId, trimmed, file as File)
        : await generateDealerPlate(dealerGroupId, trimmed)
      onSaved(plate)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed")
      setSaving(false)
    }
  }

  const generateDisabled = dealerLogo === null

  return (
    <div style={modalOverlayStyle} onClick={saving ? undefined : onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <h2 style={modalTitleStyle}>Add Plate</h2>

        <div style={modeTabsRowStyle}>
          <button
            onClick={() => setMode("upload")}
            style={modeTabButtonStyle(mode === "upload", false)}
          >
            Upload PNG
          </button>
          <button
            onClick={() => !generateDisabled && setMode("generate")}
            disabled={generateDisabled}
            title={generateDisabled
              ? "Upload a dealer logo first to enable this option"
              : undefined}
            style={modeTabButtonStyle(mode === "generate", generateDisabled)}
          >
            Generate from Logo
          </button>
        </div>

        {error && <div style={errorBannerStyle}>{error}</div>}

        <div style={fieldStyle}>
          <label style={fieldLabelStyle} htmlFor="plate-name">Plate Name</label>
          <input
            id="plate-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. "Dolan" or "CTA"'
            style={inputStyle}
          />
        </div>

        {mode === "upload" ? (
          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Plate PNG</span>
            <label style={dropZoneStyle(file !== null)}>
              <input
                type="file"
                accept={ACCEPTED_IMAGE_TYPES}
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Plate preview"
                  style={dropZonePreviewStyle}
                />
              ) : (
                <div style={dropZonePlaceholderStyle}>
                  <span style={dropZonePlaceholderTitleStyle}>
                    Click to choose a PNG
                  </span>
                  <span style={dropZonePlaceholderHintStyle}>
                    PNG or JPEG. The operator-designed plate goes here as-is.
                  </span>
                </div>
              )}
            </label>
          </div>
        ) : (
          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Source logo</span>
            {dealerLogo ? (
              <div style={logoPreviewWrapStyle}>
                <img
                  src={dealerLogo.logo_url}
                  alt="Dealer logo"
                  style={logoPreviewImageStyle}
                />
                <span style={dropZonePlaceholderHintStyle}>
                  INVI will paste this logo onto a white plate and save the
                  result as a new plate row. You can re-generate later from
                  the same logo without re-uploading.
                </span>
              </div>
            ) : (
              <div style={logoMissingStyle}>
                No dealer logo on file yet — upload one from the Dealer Plates
                tab to enable this option.
              </div>
            )}
          </div>
        )}

        <div style={actionsRowStyle}>
          <button
            onClick={onClose}
            disabled={saving}
            style={secondaryButtonStyle}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            style={primaryButtonStyle(!canSave || saving)}
          >
            {saving
              ? "Saving…"
              : mode === "upload" ? "Save Plate" : "Generate Plate"}
          </button>
        </div>
      </div>
    </div>
  )
}


// --- Styles ---

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const modalStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  width: "min(560px, 95vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  padding: THEME.spacing.lg,
  fontFamily: THEME.fonts.primary,
}

const modalTitleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
  marginBottom: THEME.spacing.md,
}

// Mode tabs (Upload PNG / Generate from Logo) mirror the page-level
// tab strip — silver underline, navy active underline, caption text.
const modeTabsRowStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.md,
  borderBottom: `1px solid ${THEME.colors.silver}`,
  marginBottom: THEME.spacing.md,
}

const modeTabButtonStyle = (active: boolean, disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: active ? 500 : 400,
  color: disabled
    ? THEME.colors.silver
    : active
      ? THEME.colors.neutral.light.textPrimary
      : THEME.colors.neutral.light.textSecondary,
  background: "transparent",
  border: "none",
  borderBottom: active ? `3px solid ${THEME.colors.navy}` : "3px solid transparent",
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: THEME.fonts.primary,
})

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  marginBottom: THEME.spacing.md,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

const dropZoneStyle = (hasFile: boolean): React.CSSProperties => ({
  display: "block",
  cursor: "pointer",
  background: THEME.colors.neutral.light.surface,
  border: hasFile
    ? `1px solid ${THEME.colors.silver}`
    : `2px dashed ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  minHeight: 180,
  overflow: "hidden",
  position: "relative",
})

const dropZonePlaceholderStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: THEME.spacing.sm,
  height: 180,
  padding: THEME.spacing.md,
  textAlign: "center",
}

const dropZonePlaceholderTitleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
}

const dropZonePlaceholderHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  maxWidth: 360,
  lineHeight: 1.4,
}

const dropZonePreviewStyle: React.CSSProperties = {
  width: "100%",
  maxHeight: 220,
  objectFit: "contain",
  display: "block",
  background: THEME.colors.lightGray,
}

const logoPreviewWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.sm,
  alignItems: "flex-start",
}

const logoPreviewImageStyle: React.CSSProperties = {
  maxWidth: 220,
  maxHeight: 120,
  objectFit: "contain",
  background: THEME.colors.lightGray,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.sm,
}

const logoMissingStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  padding: THEME.spacing.md,
  background: THEME.colors.lightGray,
  border: `1px dashed ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  lineHeight: 1.4,
}

const errorBannerStyle: React.CSSProperties = {
  background: THEME.colors.danger,
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  color: THEME.colors.semantic.danger,
  fontSize: 13,
  marginBottom: THEME.spacing.md,
}

const actionsRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
  marginTop: THEME.spacing.md,
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
})
